import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createWatcher,
  deleteWatcher,
  getWatchers,
  updateWatcher,
  type WatcherPayload,
} from '@/lib/api/watchers';

export const WATCHERS_QUERY_KEY = ['watchers'];

/**
 * Hook to fetch all watchers for the authenticated user
 */
export interface UseWatchersOptions {
  enabled?: boolean;
}

export function useWatchers({ enabled = true }: UseWatchersOptions = {}) {
  return useQuery({
    queryKey: WATCHERS_QUERY_KEY,
    queryFn: getWatchers,
    enabled,
  });
}

/**
 * Hook to create a new watcher
 */
export function useCreateWatcher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: WatcherPayload) => createWatcher(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHERS_QUERY_KEY });
    },
  });
}

/**
 * Hook to update an existing watcher
 */
export function useUpdateWatcher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      watcherId,
      payload,
    }: {
      watcherId: string;
      payload: WatcherPayload;
    }) => updateWatcher(watcherId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHERS_QUERY_KEY });
    },
  });
}

/**
 * Hook to delete a watcher
 */
export function useDeleteWatcher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (watcherId: string) => deleteWatcher(watcherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHERS_QUERY_KEY });
    },
  });
}
